import type { ReactNode } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/occurra/Button';
import { useExternalStore } from '@/hooks/useExternalStore';
import { getMomentWindow, seekToCamera } from '@/lib/cameraSeek';
import { FULL_ARCHIVE } from '@/lib/viewScope';
import type { RuleNotification } from '@/types/notifications';

import { shellCameraPick } from './shellCameraPick';

interface Props {
    alert: RuleNotification;
    toastId: string;
}

// Opens the alert's moment on its camera, and closes the alert. The button covers the whole
// toast, so a click anywhere on the alert is the pick. It lies under the toast's content, which
// lets the clicks through, so its hover reads as the toast's background. Sonner starts no swipe
// on a button: an alert that opens its moment closes by its ×.
export const ShowAlertMoment = ({ alert, toastId }: Props): ReactNode => {
    // The standalone pages have no view to open.
    const isShellMounted = useExternalStore(shellCameraPick);
    if (!isShellMounted) { return null; }
    const open = (): void => {
        const { zoomStartMs, zoomEndMs } = getMomentWindow(alert.timestampMs, alert.endTimeMs);
        const action = seekToCamera(
            shellCameraPick.select, alert.sourceDeviceId, alert.timestampMs,
            { zoom: { startMs: zoomStartMs, endMs: zoomEndMs }, scope: FULL_ARCHIVE },
        );
        if (action) { toast.dismiss(toastId); }
    };
    return (
        <Button
            aria-label="Show the moment on the camera"
            onClick={open}
            className="absolute inset-0 -z-10 h-auto rounded-[inherit]"
        />
    );
};
